import { useEffect, useRef, useState } from "react";
import { Avatar, Button, ChipPicker, EmptyState, Field, Spinner, Tag, Textarea, type TagTone } from "@cntrl/ui";
import { useCapability } from "../../app/bandContext.js";
import { ApiError } from "../../app/hooks/apiError.js";
import {
  useBandProfile,
  useSaveBandProfile,
  useSearchComparables,
  MAX_GENRES,
  MIN_COMPARABLE_QUERY,
  type BandProfileRead,
  type ComparableArtist,
  type ComparableArtistMatch,
  type ComparableTier,
} from "../../app/hooks/useBandProfile.js";
import { orgInitials } from "../../app/hooks/useOrg.js";
import { GENRES } from "./genres.js";
import { SpotifyArtistLink, SpotifyAttribution } from "../shared/VendorAttribution.js";
import shell from "./SettingsScreen.module.scss";
import styles from "./SettingsProfileTab.module.scss";

interface Draft {
  genres: string[];
  comparableArtists: ComparableArtist[];
  soundDescription: string;
  targetAudience: string;
  goals: string;
  whatsWorking: string;
  brandVoice: string;
  thingsToAvoid: string;
  openToSupport: boolean;
}

const EMPTY_DRAFT: Draft = {
  genres: [],
  comparableArtists: [],
  soundDescription: "",
  targetAudience: "",
  goals: "",
  whatsWorking: "",
  brandVoice: "",
  thingsToAvoid: "",
  openToSupport: true,
};

function toDraft(p: BandProfileRead): Draft {
  return {
    genres: p.genres ?? [],
    // Carry every stored field through the draft. The PUT is a full replace, so dropping one here would silently
    // strip it on the next save — the legacy Songstats pair is never written any more and is round-tripped for
    // exactly that reason, and the Spotify id is what the pill's link back to Spotify reads.
    comparableArtists: (p.comparableArtists ?? []).map((c) => ({
      name: c.name ?? "",
      tier: c.tier ?? "",
      songstatsArtistId: c.songstatsArtistId ?? null,
      monthlyListeners: c.monthlyListeners ?? null,
      fetchedOn: c.fetchedOn ?? null,
      spotifyArtistId: c.spotifyArtistId ?? null,
    })),
    soundDescription: p.soundDescription ?? "",
    targetAudience: p.targetAudience ?? "",
    goals: p.goals ?? "",
    whatsWorking: p.whatsWorking ?? "",
    brandVoice: p.brandVoice ?? "",
    thingsToAvoid: p.thingsToAvoid ?? "",
    openToSupport: p.openToSupport ?? true,
  };
}

/**
 * Artist Profile tab — the band-scoped `/settings/profile` surface: genre chips (≤ 3, enforced in
 * UI), comparable artists with a validation tier colour, the AI/discovery prose (sound / audience / goals /
 * what's-working / brand voice), and the "open to support opportunities" booking-discovery toggle (born-blank ON,
 * ). GET/PUT are band:manage-gated; a non-manager sees the designed 403 state. The save PUTs the whole
 * tab and reseeds from the canonical response — never a phantom success. Designed empty/loading/error.
 */
export function SettingsProfileTab() {
  const canManage = useCapability("band:manage");
  const profile = useBandProfile();
  const save = useSaveBandProfile();

  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const hydrated = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (hydrated.current || !profile.data) return;
    setDraft(toDraft(profile.data));
    hydrated.current = true;
  }, [profile.data]);

  function patch<K extends keyof Draft>(key: K, value: Draft[K]) {
    setDraft((d) => ({ ...d, [key]: value }));
    setSaved(false);
  }

  function onSave() {
    setError(null);
    setSaved(false);
    const body = {
      genres: draft.genres,
      comparableArtists: draft.comparableArtists
        .map((c) => ({ ...c, name: (c.name ?? "").trim(), tier: c.tier ?? "" }))
        .filter((c) => c.name.length > 0),
      soundDescription: draft.soundDescription.trim() || null,
      targetAudience: draft.targetAudience.trim() || null,
      goals: draft.goals.trim() || null,
      whatsWorking: draft.whatsWorking.trim() || null,
      brandVoice: draft.brandVoice.trim() || null,
      thingsToAvoid: draft.thingsToAvoid.trim() || null,
      openToSupport: draft.openToSupport,
    };
    save.mutate(body, {
      onSuccess: (data) => {
        setDraft(toDraft(data));
        setSaved(true);
      },
      onError: (err) => setError(err instanceof Error && err.message ? err.message : "Couldn't save your profile."),
    });
  }

  if (profile.isLoading) {
    return (
      <div className={shell.center} role="status" aria-live="polite">
        <Spinner size={20} label="Loading profile…" />
      </div>
    );
  }
  if (profile.isError) {
    const status = profile.error instanceof ApiError ? profile.error.status : 0;
    if (status === 403 || !canManage) {
      return (
        <EmptyState
          title="Profile unavailable"
          description="Your role on this band doesn't include editing the artist profile."
        />
      );
    }
    return (
      <EmptyState
        title="Couldn't load your profile"
        description={profile.error instanceof Error ? profile.error.message : "Please try again."}
        action={<Button onClick={() => void profile.refetch()}>Retry</Button>}
      />
    );
  }

  return (
    <div className={shell.stack}>
      {/* Genres — the public directory field, capped at 3. A type-ahead over the `GENRES` list (genres.ts) on top of
          the free-string `band_profiles.genres` — there is no server genre enum (the endpoint owns the field, the FE
          owns the picker). */}
      <section aria-labelledby="profile-genres-heading" className={styles.group}>
        <h3 id="profile-genres-heading" className={`${shell.groupLabel} ${styles.groupTitle}`}>
          Genres
        </h3>
        <GenrePicker selected={draft.genres} max={MAX_GENRES} onChange={(genres) => patch("genres", genres)} />
      </section>

      {/* Comparable artists — picked from Spotify search. No colour on a picked artist: Spotify's policy
          forbids benchmarking off its data, so a pill is identity only (older pills keep their colour). */}
      <section aria-labelledby="profile-comparables-heading" className={styles.group}>
        <h3 id="profile-comparables-heading" className={`${shell.groupLabel} ${styles.groupTitle}`}>
          Comparable artists
        </h3>
        <ComparablesEditor
          comparables={draft.comparableArtists}
          onChange={(comparableArtists) => patch("comparableArtists", comparableArtists)}
        />
      </section>

      {/* Discovery prose. No group heading (PO, 2026-08-19): each field's own label already says what it is,
          so "Your story" was a title over six self-titled things. The <section> stays for grouping but is
          deliberately unlabelled now — an aria-labelledby pointing at a removed heading names nothing. */}
      <section className={styles.prose}>
        <ProseField
          label="Describe Your Sound"
          subLabel="In one sentence, what do you actually sound like?"
          value={draft.soundDescription}
          onChange={(v) => patch("soundDescription", v)}
        />
        <ProseField
          label="Target audience"
          subLabel="Who's your ideal fan? Age, vibe, where they hang out."
          value={draft.targetAudience}
          onChange={(v) => patch("targetAudience", v)}
        />
        <ProseField
          label="Current Goals"
          subLabel="What are you trying to accomplish right now?"
          value={draft.goals}
          onChange={(v) => patch("goals", v)}
        />
        <ProseField
          label="What's working"
          subLabel="What content has performed best for you so far?"
          value={draft.whatsWorking}
          onChange={(v) => patch("whatsWorking", v)}
        />
        <ProseField
          label="Describe Your Brand Voice"
          subLabel="Your tone and personality on social media."
          value={draft.brandVoice}
          onChange={(v) => patch("brandVoice", v)}
        />
        <ProseField
          label="Things To Avoid"
          subLabel="What you never want to sound like or be associated with."
          value={draft.thingsToAvoid}
          onChange={(v) => patch("thingsToAvoid", v)}
        />
      </section>

      {/* Booking discovery moved to the ACCOUNT tab (spec placement, 2026-08-19) — it still writes THIS
          row, which is why every save here goes through `toUpdateRequest`. */}

      {error && (
        <p className={shell.error} role="alert">
          {error}
        </p>
      )}
      {saved && (
        <p className={shell.success} role="status">
          Profile saved.
        </p>
      )}

      <div className={shell.formActions} data-align="end">
        <Button variant="accent" onClick={onSave} loading={save.isPending}>
          Save profile
        </Button>
      </div>
    </div>
  );
}

// ---- genre picker ---------------------------------------------------------------------------------------

// Every option is precomputed once: 250 genres, filtered per render only by what is already chosen.
const GENRE_OPTIONS = GENRES.map((g) => ({ value: g, label: g }));

/**
 * The genre type-ahead: pick from `GENRES`, each choice drops below as a removable chip, and the control hides at
 * the cap (`ChipPicker`'s `max`) so a fourth can't be typed. A saved value that is not in the list (a migrated or
 * earlier-vocabulary string) is just another chip — removable, and never silently dropped by a save.
 */
function GenrePicker({
  selected,
  max,
  onChange,
}: {
  selected: string[];
  max: number;
  onChange: (next: string[]) => void;
}) {
  const chosen = new Set(selected.map((x) => x.toLowerCase()));
  return (
    <ChipPicker
      label="Genres"
      hideLabel // the section's own heading already names the field
      placeholder="Search genres…"
      options={GENRE_OPTIONS.filter((o) => !chosen.has(o.value.toLowerCase()))}
      chips={selected.map((g) => ({ value: g, label: g }))}
      max={max}
      chipSize="md"
      onAdd={(g) => {
        if (selected.length < max) onChange([...selected, g]);
      }}
      onRemove={(g) => onChange(selected.filter((x) => x !== g))}
    />
  );
}

// ---- comparable artists editor -------------------------------------------------------------------------

/** What a LEGACY pill's colour means, in words — for anyone who cannot use hue. Only pre-Spotify pills carry a
 * colour (a Songstats listener count, or one picked by hand); a Spotify-picked pill is uncoloured. */
const TIER_LABEL: Record<ComparableTier, string> = {
  "": "",
  green: "Strong following",
  yellow: "Decent following",
  red: "Limited following",
};

/** The tier vocabulary is the SERVER's (green/yellow/red); `Tag` speaks in semantic tones. Mapping here keeps
 * the colour names out of the shared primitive — a tag does not know what a "yellow comparable" is. */
const TIER_TONE: Record<ComparableTier, TagTone> = {
  "": "neutral",
  green: "success",
  yellow: "warning",
  red: "danger",
};

/** 1.7M / 89.4K / 555. Null renders NOTHING rather than "0", which would be a different and false claim. */
function formatCount(count: number | null | undefined): string | null {
  if (count === null || count === undefined) return null;
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
  if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`;
  return `${count}`;
}

/** A LEGACY pill's stored figure (Songstats monthly listeners). Spotify-picked pills carry none. */
function pillMeta(c: ComparableArtist): string | undefined {
  const listeners = formatCount(c.monthlyListeners);
  return listeners ? `${listeners} listeners` : undefined;
}

/**
 * The comparable-artist editor: pills above a search box.
 *
 * **Search, then pick.** Enter searches the Spotify catalogue and lists candidates with their photos, so two
 * artists sharing a name are told apart by the band rather than resolved for them. The pill stores the Spotify
 * name + id; Spotify's policy requires a link back wherever its metadata is shown, so each Spotify pill carries
 * one.
 */
function ComparablesEditor({
  comparables,
  onChange,
}: {
  comparables: ComparableArtist[];
  onChange: (next: ComparableArtist[]) => void;
}) {
  const [query, setQuery] = useState("");
  const [failure, setFailure] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<ComparableArtistMatch[]>([]);

  const search = useSearchComparables();
  const tooShort = query.trim().length < MIN_COMPARABLE_QUERY;

  async function runSearch() {
    const q = query.trim();
    if (q.length < MIN_COMPARABLE_QUERY || search.isPending) return;
    setFailure(null);
    setCandidates([]);

    let response;
    try {
      response = await search.mutateAsync(q);
    } catch {
      setFailure("Couldn't reach Spotify just now. Try again in a moment.");
      return;
    }

    // Two empty answers, two messages: "we cannot search" is an operator problem, "no matches" is a typo.
    if (!response.providerConfigured) {
      setFailure("Artist search is unavailable right now.");
      return;
    }
    const items = (response.items ?? []).filter((m) => (m.name ?? "").trim() && m.spotifyArtistId);
    if (items.length === 0) {
      setFailure(`No artists found for "${q}".`);
      return;
    }
    setCandidates(items);
  }

  function pick(m: ComparableArtistMatch) {
    const name = (m.name ?? "").trim();
    // Adding the same artist twice is a no-op wearing the costume of an action.
    if (comparables.some((c) => c.spotifyArtistId === m.spotifyArtistId)) {
      setFailure(`${name} is already on your list.`);
      return;
    }
    setQuery("");
    setCandidates([]);
    setFailure(null);
    onChange([...comparables, { name, tier: "", spotifyArtistId: m.spotifyArtistId }]);
  }

  function remove(index: number) {
    onChange(comparables.filter((_, i) => i !== index));
  }

  return (
    <div className={styles.comparables}>
      {comparables.length > 0 && (
        <ul className={styles.pillRow}>
          {comparables.map((c, i) => {
            const tier = (c.tier ?? "") as ComparableTier;
            return (
              <li key={c.spotifyArtistId ?? c.songstatsArtistId ?? `${c.name}-${i}`} className={styles.pillItem}>
                <Tag
                  tone={TIER_TONE[tier]}
                  size="md"
                  title={TIER_LABEL[tier] || undefined}
                  meta={pillMeta(c)}
                  removeLabel={c.name ?? undefined}
                  onRemove={() => remove(i)}
                >
                  {c.name}
                </Tag>
                {c.spotifyArtistId && <SpotifyArtistLink spotifyArtistId={c.spotifyArtistId} name={c.name ?? ""} />}
              </li>
            );
          })}
        </ul>
      )}

      {/* One small <form> around the box and its button, per the rapid-add contract in COMPONENTS.md: it makes
          Enter-in-the-field and clicking the button the SAME handler rather than two that drift apart. */}
      <form
        className={styles.searchBox}
        onSubmit={(e) => {
          e.preventDefault();
          void runSearch();
        }}
      >
        <Field
          label="Search for a comparable artist"
          hideLabel
          className={styles.comparableSearch}
          placeholder="Search for an artist and press Enter…"
          value={query}
          disabled={search.isPending}
          onChange={(e) => {
            setQuery(e.target.value);
            setFailure(null);
          }}
        />
        <Button type="submit" size="field" disabled={tooShort || search.isPending}>
          Search
        </Button>
      </form>

      {search.isPending && <p className={styles.hint}>Searching for {query.trim()}…</p>}
      {failure && (
        <p className={styles.searchError} role="alert">
          {failure}
        </p>
      )}
      {candidates.length > 0 && (
        <div className={styles.candidates}>
          <ul className={styles.candidateList} aria-label="Matching artists">
            {candidates.map((m) => (
              <li key={m.spotifyArtistId} className={styles.candidate}>
                <button type="button" className={styles.candidatePick} onClick={() => pick(m)}>
                  <Avatar initials={orgInitials(m.name ?? "")} src={m.imageUrl ?? null} size="sm" shape="circle" />
                  <span className={styles.candidateName}>{m.name}</span>
                </button>
                <SpotifyArtistLink spotifyArtistId={m.spotifyArtistId!} name={m.name ?? ""} />
              </li>
            ))}
          </ul>
          <div className={styles.candidateFooter}>
            <SpotifyAttribution />
          </div>
        </div>
      )}
    </div>
  );
}

// ---- prose field ---------------------------------------------------------------------------------------

function ProseField({
  label,
  subLabel,
  value,
  onChange,
}: {
  label: string;
  subLabel: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <Textarea
      label={label}
      labelVariant="heading"
      subLabel={subLabel}
      rows={2}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}
